import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flightLogRecognitionApi } from './api';

export const DEFAULT_CURSOR_LIMIT = 20;
const MAX_CURSOR_LIMIT = 200;

/**
 * 判断记录是否处于在途识别或 Shadow 重识别在途状态（D8、D46、T12）
 */
export const isInFlightRecord = (row) => {
    if (!row || row.isPlaceholderRow) return false;
    return (
        row.status === 'pending'
        || row.status === 'recognizing'
        || Boolean(row.pending_reextract)
        || Boolean(row.reextract_queued)
        || row.reextract_mode === 'shadow'
    );
};

/**
 * 归一化列表接口响应
 */
const normalizeCursorResponse = (res) => {
    const payload = res?.data || res || {};
    const dataBlock = payload.data && Array.isArray(payload.data.items) ? payload.data : payload;
    const items = Array.isArray(dataBlock.items)
        ? dataBlock.items
        : Array.isArray(payload)
            ? payload
            : [];
    return {
        items,
        total: Number(dataBlock.total ?? items.length ?? 0),
        nextCursor: dataBlock.next_cursor ?? null,
        hasMore: Boolean(dataBlock.has_more),
        chainComputedAt: dataBlock.chain_computed_at ?? null,
        chainIsComputing: Boolean(dataBlock.chain_is_computing),
        view: dataBlock.view || 'all',
        exactReg: dataBlock.exact_reg ?? null,
    };
};

/**
 * 根据筛选条件与二级页签状态构造查询参数（D55、T03、T14、T38）
 */
export const buildFlightLogScopeParams = (flt = {}, tab = { view: 'all', exactReg: null }) => {
    const view = tab?.view || 'all';
    const params = {
        view,
    };
    if (view === 'reg' && tab?.exactReg) {
        params.exact_reg = tab.exactReg;
    }
    if (flt.keyword?.trim()) {
        params.keyword = flt.keyword.trim();
    }
    if (flt.status) {
        params.status = flt.status;
    }
    if (flt.source_type) {
        params.source_type = flt.source_type;
    }
    // D55: 顶部模糊 aircraft_reg 筛选仅在「全部」页签生效
    if (view === 'all' && flt.aircraft_reg) {
        params.aircraft_reg = flt.aircraft_reg;
    }
    if (flt.dateRange?.[0]) {
        params.date_start = flt.dateRange[0].format('YYYY-MM-DD');
    }
    if (flt.dateRange?.[1]) {
        params.date_end = flt.dateRange[1].format('YYYY-MM-DD');
    }
    return params;
};

/**
 * 构造二级机号页签接口参数（T05：忽略顶部模糊 aircraft_reg）
 */
export const buildAircraftTabsParams = (flt = {}) => {
    const params = {};
    if (flt.keyword?.trim()) params.keyword = flt.keyword.trim();
    if (flt.status) params.status = flt.status;
    if (flt.source_type) params.source_type = flt.source_type;
    if (flt.dateRange?.[0]) params.date_start = flt.dateRange[0].format('YYYY-MM-DD');
    if (flt.dateRange?.[1]) params.date_end = flt.dateRange[1].format('YYYY-MM-DD');
    return params;
};

/**
 * Flight Log 游标无限滚动、二级机号页签、在途 4 秒按批刷新与占位行状态管理 Hook（T08、T09、T11、T12）
 */
export const useFlightLogInfiniteScroll = ({
    initialFilters,
    onClearSelection,
    onListLoadError,
    onInFlightRecordUpdated,
}) => {
    const [filters, setFilters] = useState(
        initialFilters || {
            keyword: '',
            status: undefined,
            source_type: undefined,
            aircraft_reg: '',
            dateRange: null,
        }
    );
    const appliedFiltersRef = useRef(filters);

    const [activeTab, setActiveTab] = useState({ view: 'all', exactReg: null });
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;

    const [aircraftTabs, setAircraftTabs] = useState({
        aircraftRegs: [],
        hasUnassigned: false,
    });

    const [data, setData] = useState([]);
    const dataRef = useRef(data);
    dataRef.current = data;

    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [chainComputedAt, setChainComputedAt] = useState(null);
    const [chainIsComputing, setChainIsComputing] = useState(false);
    const [chainOrderStale, setChainOrderStale] = useState(false);

    // 用于丢弃过期的并发列表请求
    const requestSeqRef = useRef(0);
    // 当前会话内被删除/作废/取代的占位行快照：key = record.id -> { record, index }
    const placeholderMapRef = useRef(new Map());
    // 表格外层容器 ref，用于监听 .ant-table-body 滚动与恢复滚动位置
    const tableContainerRef = useRef(null);

    const getScrollBody = useCallback(() => {
        if (!tableContainerRef.current) return null;
        return tableContainerRef.current.querySelector('.ant-table-body');
    }, []);

    const scrollToTop = useCallback(() => {
        const body = getScrollBody();
        if (body) {
            body.scrollTop = 0;
        }
    }, [getScrollBody]);

    const fetchAircraftTabs = useCallback(async (flt = appliedFiltersRef.current) => {
        try {
            const res = await flightLogRecognitionApi.aircraftTabs(buildAircraftTabsParams(flt));
            const payload = res?.data || res || {};
            const dataBlock = payload.data && Array.isArray(payload.data.aircraft_regs) ? payload.data : payload;
            setAircraftTabs({
                aircraftRegs: Array.isArray(dataBlock.aircraft_regs) ? dataBlock.aircraft_regs : [],
                hasUnassigned: Boolean(dataBlock.has_unassigned),
            });
        } catch (_) {
            // 页签列表静默降级，不打断主列表
        }
    }, []);

    /**
     * 将当前视图中已标记的灰色占位行合并回刷新后的列表中，避免在未切换页签/筛选前消失（D16、T11③）
     */
    const mergePlaceholders = useCallback((freshItems) => {
        const map = placeholderMapRef.current;
        if (!map || map.size === 0) return freshItems;
        const freshById = new Map(freshItems.map((item) => [item.id, item]));
        const result = freshItems.map((item) => {
            if (map.has(item.id)) {
                const entry = map.get(item.id);
                return {
                    ...item,
                    ...entry.record,
                    isPlaceholderRow: true,
                };
            }
            return item;
        });

        // 对于后端已物理/逻辑删除不再返回的记录，按原位置插回灰色占位行
        const missingEntries = Array.from(map.values())
            .filter((entry) => !freshById.has(entry.record.id))
            .sort((a, b) => a.index - b.index);

        missingEntries.forEach((entry) => {
            const insertAt = Math.min(Math.max(0, entry.index), result.length);
            result.splice(insertAt, 0, entry.record);
        });
        return result;
    }, []);

    /**
     * 重置并从首屏加载（切换二级页签、修改筛选、点击顶部立即刷新时调用）
     */
    const fetchInitial = useCallback(
        async ({
            nextFilters = appliedFiltersRef.current,
            nextTab = activeTabRef.current,
            resetScroll = true,
            clearPlaceholders = true,
            refreshTabs = true,
        } = {}) => {
            const reqId = ++requestSeqRef.current;
            appliedFiltersRef.current = nextFilters;
            if (clearPlaceholders) {
                placeholderMapRef.current = new Map();
            }
            setLoading(true);
            setLoadingMore(false);
            setLoadMoreError(null);
            setChainOrderStale(false);

            if (refreshTabs) {
                fetchAircraftTabs(nextFilters);
            }

            try {
                const params = {
                    ...buildFlightLogScopeParams(nextFilters, nextTab),
                    limit: DEFAULT_CURSOR_LIMIT,
                };
                const res = await flightLogRecognitionApi.list(params);
                if (reqId !== requestSeqRef.current) return;
                const parsed = normalizeCursorResponse(res);
                setData(clearPlaceholders ? parsed.items : mergePlaceholders(parsed.items));
                setTotal(parsed.total);
                setNextCursor(parsed.nextCursor);
                setHasMore(parsed.hasMore);
                setChainComputedAt(parsed.chainComputedAt);
                setChainIsComputing(parsed.chainIsComputing);
                setChainOrderStale(false);
                if (resetScroll) {
                    scrollToTop();
                }
            } catch (err) {
                if (reqId !== requestSeqRef.current) return;
                if (onListLoadError) {
                    onListLoadError(err);
                }
            } finally {
                if (reqId === requestSeqRef.current) {
                    setLoading(false);
                }
            }
        },
        [fetchAircraftTabs, mergePlaceholders, onListLoadError, scrollToTop]
    );

    /**
     * 游标追加下一批（T09①、T09④、D48）
     */
    const loadMore = useCallback(async () => {
        if (loading || loadingMore || !hasMore || !nextCursor) return;
        const reqId = requestSeqRef.current;
        setLoadingMore(true);
        setLoadMoreError(null);

        try {
            const params = {
                ...buildFlightLogScopeParams(appliedFiltersRef.current, activeTabRef.current),
                cursor: nextCursor,
                limit: DEFAULT_CURSOR_LIMIT,
            };
            const res = await flightLogRecognitionApi.list(params);
            if (reqId !== requestSeqRef.current) return;
            const parsed = normalizeCursorResponse(res);

            // D48: 按记录 id 去重合并，并保留 D16 灰色占位行标记
            setData((prev) => {
                const seen = new Set(prev.map((row) => row.id));
                const appended = parsed.items.filter((row) => row?.id && !seen.has(row.id));
                return mergePlaceholders([...prev, ...appended]);
            });
            setTotal(parsed.total);
            setNextCursor(parsed.nextCursor);
            setHasMore(parsed.hasMore);
            setChainIsComputing(parsed.chainIsComputing);

            // D48 / T09⑤: 机号页签下若后续批次返回的 chain_computed_at 与首批不一致，提示「顺序已更新，点击刷新」
            if (
                activeTabRef.current.view === 'reg'
                && chainComputedAt
                && parsed.chainComputedAt
                && parsed.chainComputedAt !== chainComputedAt
            ) {
                setChainOrderStale(true);
            }
        } catch (err) {
            if (reqId !== requestSeqRef.current) return;
            const msg = err?.response?.data?.detail || err?.message || '加载下一批记录失败';
            setLoadMoreError(typeof msg === 'string' ? msg : '加载下一批记录失败');
        } finally {
            if (reqId === requestSeqRef.current) {
                setLoadingMore(false);
            }
        }
    }, [chainComputedAt, hasMore, loading, loadingMore, mergePlaceholders, nextCursor]);

    /**
      * 保持当前滚动位置重拉已加载范围（T11②、T12③、D39）
      * 用于：抽屉保存/确认/采纳建议后刷新同机号链序，或在途记录识别完成后入链重排。
      */
    const refreshLoadedRange = useCallback(
        async ({ clearPlaceholders = false } = {}) => {
            const reqId = ++requestSeqRef.current;
            const body = getScrollBody();
            const prevScrollTop = body ? body.scrollTop : 0;
            const loadedCount = Math.max(
                DEFAULT_CURSOR_LIMIT,
                dataRef.current.filter((r) => !r?.isPlaceholderRow).length
            );

            if (clearPlaceholders) {
                placeholderMapRef.current = new Map();
            }

            fetchAircraftTabs(appliedFiltersRef.current);

            try {
                const baseScopeParams = buildFlightLogScopeParams(appliedFiltersRef.current, activeTabRef.current);
                const collectedItems = [];
                const seenIds = new Set();
                let currentCursor = undefined;
                let latestParsed = null;

                while (collectedItems.length < loadedCount) {
                    const remaining = loadedCount - collectedItems.length;
                    const batchLimit = Math.min(MAX_CURSOR_LIMIT, Math.max(1, remaining));
                    const params = {
                        ...baseScopeParams,
                        limit: batchLimit,
                        ...(currentCursor ? { cursor: currentCursor } : {}),
                    };
                    const res = await flightLogRecognitionApi.list(params);
                    if (reqId !== requestSeqRef.current) return;
                    const parsed = normalizeCursorResponse(res);
                    latestParsed = parsed;
                    for (const row of parsed.items) {
                        if (row?.id && !seenIds.has(row.id)) {
                            seenIds.add(row.id);
                            collectedItems.push(row);
                        }
                    }
                    if (!parsed.hasMore || !parsed.nextCursor || parsed.items.length === 0) {
                        break;
                    }
                    currentCursor = parsed.nextCursor;
                }

                if (!latestParsed || reqId !== requestSeqRef.current) return;
                const mergedItems = clearPlaceholders ? collectedItems : mergePlaceholders(collectedItems);
                setData(mergedItems);
                setTotal(latestParsed.total);
                setNextCursor(latestParsed.nextCursor);
                setHasMore(latestParsed.hasMore);
                setChainComputedAt(latestParsed.chainComputedAt);
                setChainIsComputing(latestParsed.chainIsComputing);
                setChainOrderStale(false);
                setLoadMoreError(null);

                if (body && typeof window !== 'undefined' && window.requestAnimationFrame) {
                    window.requestAnimationFrame(() => {
                        const currentBody = getScrollBody();
                        if (currentBody) {
                            currentBody.scrollTop = prevScrollTop;
                        }
                    });
                }
            } catch (_) {
                // 后台保持位置重拉失败时不清空已有列表
            }
        },
        [fetchAircraftTabs, getScrollBody, mergePlaceholders]
    );

    /**
     * 切换二级页签（T08④、T09②、D56）：清空勾选、清除灰色占位行、重置回顶部
     */
    const selectTab = useCallback(
        (nextTab) => {
            const normalizedTab = {
                view: nextTab?.view || 'all',
                exactReg: nextTab?.view === 'reg' ? nextTab.exactReg || null : null,
            };
            setActiveTab(normalizedTab);
            activeTabRef.current = normalizedTab;
            if (onClearSelection) {
                onClearSelection();
            }
            fetchInitial({
                nextFilters: appliedFiltersRef.current,
                nextTab: normalizedTab,
                resetScroll: true,
                clearPlaceholders: true,
                refreshTabs: false,
            });
        },
        [fetchInitial, onClearSelection]
    );

    /**
     * 顶部筛选提交/变更（T09②）
     */
    const applyFilterPatch = useCallback(
        (patch) => {
            const nextFilters = { ...filters, ...patch };
            setFilters(nextFilters);
            fetchInitial({
                nextFilters,
                nextTab: activeTabRef.current,
                resetScroll: true,
                clearPlaceholders: true,
                refreshTabs: true,
            });
        },
        [fetchInitial, filters]
    );

    const submitSearch = useCallback(() => {
        fetchInitial({
            nextFilters: filters,
            nextTab: activeTabRef.current,
            resetScroll: true,
            clearPlaceholders: true,
            refreshTabs: true,
        });
    }, [fetchInitial, filters]);

    const resetFilters = useCallback(() => {
        const nextFilters = {
            keyword: '',
            status: undefined,
            source_type: undefined,
            aircraft_reg: '',
            dateRange: null,
        };
        setFilters(nextFilters);
        fetchInitial({
            nextFilters,
            nextTab: activeTabRef.current,
            resetScroll: true,
            clearPlaceholders: true,
            refreshTabs: true,
        });
    }, [fetchInitial]);

    /**
     * 原地替换单条记录（不改变滚动位置）
     */
    const replaceRecord = useCallback((record) => {
        if (!record?.id) return;
        setData((prev) => prev.map((row) => (row.id === record.id ? { ...row, ...record } : row)));
    }, []);

    /**
     * 将被删除、整页作废或去重取代的行在当前视图中转为灰色占位行（D16、T11③、AC-S3-08）
     */
    const markRecordsAsPlaceholder = useCallback(
        (ids, placeholderText = '该记录已删除，切换页签或刷新后移除', extraPatch = {}) => {
            if (!Array.isArray(ids) || ids.length === 0) return;
            const idSet = new Set(ids);
            let newlyMarked = 0;

            setData((prev) =>
                prev.map((row, index) => {
                    if (!idSet.has(row.id)) return row;
                    if (!row.isPlaceholderRow) {
                        newlyMarked += 1;
                    }
                    const placeholderRow = {
                        ...row,
                        ...extraPatch,
                        isPlaceholderRow: true,
                        placeholderText,
                    };
                    placeholderMapRef.current.set(row.id, {
                        record: placeholderRow,
                        index,
                    });
                    return placeholderRow;
                })
            );

            if (newlyMarked > 0 && extraPatch.decrementTotal !== false) {
                setTotal((t) => Math.max(0, t - newlyMarked));
            }
        },
        []
    );

    // 首次进入页面时加载首屏与机号页签
    useEffect(() => {
        fetchInitial({
            nextFilters: appliedFiltersRef.current,
            nextTab: activeTabRef.current,
            resetScroll: true,
            clearPlaceholders: true,
            refreshTabs: true,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 监听 .ant-table-body 滚动到底部自动加载下一批（T09①）
    useEffect(() => {
        const body = getScrollBody();
        if (!body) return undefined;

        const handleScroll = () => {
            if (loading || loadingMore || !hasMore || loadMoreError) return;
            const remaining = body.scrollHeight - body.scrollTop - body.clientHeight;
            if (remaining <= 80) {
                loadMore();
            }
        };

        body.addEventListener('scroll', handleScroll, { passive: true });
        return () => body.removeEventListener('scroll', handleScroll);
    }, [getScrollBody, hasMore, loadMore, loadMoreError, loading, loadingMore, data.length]);

    // T12: 在途行每 4 秒按 ID 批量轮询，仅原地替换在途记录；一旦有记录离开在途状态则触发当前已加载范围重拉
    const inFlightIds = useMemo(
        () => data.filter(isInFlightRecord).map((row) => row.id),
        [data]
    );

    useEffect(() => {
        if (inFlightIds.length === 0) return undefined;

        const timer = setInterval(async () => {
            try {
                const res = await flightLogRecognitionApi.batchByIds(inFlightIds);
                const payload = res?.data || res || {};
                const dataBlock = payload.data && Array.isArray(payload.data.items) ? payload.data : payload;
                const items = Array.isArray(dataBlock.items) ? dataBlock.items : [];
                const byId = new Map(items.map((item) => [item.id, item]));

                let anyFinished = false;
                inFlightIds.forEach((id) => {
                    const updated = byId.get(id);
                    if (!updated || !isInFlightRecord(updated)) {
                        anyFinished = true;
                    }
                });

                if (items.length > 0) {
                    setData((prev) =>
                        prev.map((row) => {
                            if (row.isPlaceholderRow) return row;
                            const updated = byId.get(row.id);
                            return updated ? { ...row, ...updated } : row;
                        })
                    );
                    if (onInFlightRecordUpdated) {
                        items.forEach((item) => onInFlightRecordUpdated(item));
                    }
                }

                if (anyFinished) {
                    refreshLoadedRange({ clearPlaceholders: false });
                }
            } catch (_) {
                // 静默轮询失败不打断用户
            }
        }, 4000);

        return () => clearInterval(timer);
    }, [inFlightIds, onInFlightRecordUpdated, refreshLoadedRange]);

    const [recomputingChains, setRecomputingChains] = useState(false);

    const recomputeChains = useCallback(
        async ({ onlyCurrentReg = false, onSuccess, onError } = {}) => {
            setRecomputingChains(true);
            try {
                const curTab = activeTabRef.current;
                const payload = onlyCurrentReg && curTab?.view === 'reg' && curTab?.exactReg
                    ? { regs: [curTab.exactReg] }
                    : {};
                const res = await flightLogRecognitionApi.recomputeChains(payload);
                const result = res?.data || res || {};
                await fetchInitial({ resetScroll: false, clearPlaceholders: true, refreshTabs: true });
                if (onSuccess) {
                    onSuccess(result, { onlyCurrentReg, exactReg: curTab?.exactReg });
                }
                return result;
            } catch (err) {
                if (onError) {
                    onError(err);
                } else if (onListLoadError) {
                    onListLoadError(err);
                }
                return null;
            } finally {
                setRecomputingChains(false);
            }
        },
        [fetchInitial, onListLoadError]
    );

    const getCurrentScopeParams = useCallback(
        () => buildFlightLogScopeParams(appliedFiltersRef.current, activeTabRef.current),
        []
    );

    return {
        filters,
        setFilters,
        activeTab,
        aircraftTabs,
        data,
        setData,
        total,
        loading,
        loadingMore,
        loadMoreError,
        hasMore,
        chainComputedAt,
        chainIsComputing,
        chainOrderStale,
        recomputingChains,
        tableContainerRef,
        fetchInitial,
        loadMore,
        refreshLoadedRange,
        recomputeChains,
        selectTab,
        applyFilterPatch,
        submitSearch,
        resetFilters,
        replaceRecord,
        markRecordsAsPlaceholder,
        getCurrentScopeParams,
    };
};

export default useFlightLogInfiniteScroll;
